import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, subMonths, format } from 'date-fns';
import { buildUtcMonthBuckets, assignToBucket, fetchAllProcurementsInRange } from '@/lib/procurement-trend-utils';

export interface MonthlyTrendPoint {
  month: string;
  value: number;
}

export interface AdminDashboardStats {
  // Procurement pipeline
  procurementsByStatus: Record<string, number>;
  totalProcurementValue: number;
  monthlyProcurementValue: number;
  recentProcurements: any[];
  procurementTrend: MonthlyTrendPoint[];

  // Finance
  totalExpensesThisMonth: number;
  totalExpensesLastMonth: number;
  pendingPaymentRequests: number;
  pendingPaymentAmount: number;
  approvedAwaitingPayment: number;

  // B2B Sales
  b2bSalesThisMonth: number;
  b2bSalesLastMonth: number;

  // Inventory
  totalProducts: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalStockValue: number;

  // Farms & Harvests
  totalFarms: number;
  activeHarvests: number;

  // Users & Alerts
  totalUsers: number;
  unreadAlerts: number;
  criticalAlerts: any[];

  // Quality
  pendingQualityChecks: number;

  // Exchange Rate
  latestExchangeRate: { rate: number; currency_pair: string; effective_date: string } | null;

  // GRN
  pendingGRNCount: number;
}

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: async (): Promise<AdminDashboardStats> => {
      const now = new Date();
      const thisMonthStart = startOfMonth(now).toISOString();
      const lastMonthStart = startOfMonth(subMonths(now, 1)).toISOString();
      const lastMonthEnd = startOfMonth(now).toISOString();

      // Build 6 UTC month buckets for trend
      const trendBuckets = buildUtcMonthBuckets(6);
      const sixMonthsAgo = trendBuckets[0].start.toISOString();

      const results = await Promise.all([
        supabase.from('procurements').select('id, status, total_amount, total_quantity, product_name, created_at, source_type').order('created_at', { ascending: false }),
        supabase.from('expenses').select('total_amount').gte('expense_date', thisMonthStart.split('T')[0]),
        supabase.from('expenses').select('total_amount').gte('expense_date', lastMonthStart.split('T')[0]).lt('expense_date', lastMonthEnd.split('T')[0]),
        supabase.from('payment_requests').select('id, status, total_amount'),
        supabase.from('inventory_stock').select('product_id, current_quantity, reorder_level'),
        supabase.from('farms').select('id', { count: 'exact', head: true }),
        supabase.from('harvests').select('id, status'),
        supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
        supabase.from('alerts').select('id, type, message, is_read, action_taken'),
        (supabase.from('procurement_quality').select('id, quality_status') as any).eq('quality_status', 'pending'),
        // B2B Sales this month
        supabase.from('b2b_sales_data').select('total_revenue').gte('sale_date', thisMonthStart.split('T')[0]),
        // B2B Sales last month
        supabase.from('b2b_sales_data').select('total_revenue').gte('sale_date', lastMonthStart.split('T')[0]).lt('sale_date', lastMonthEnd.split('T')[0]),
        // Exchange rate (latest)
        supabase.from('exchange_rates' as any).select('rate, currency_pair, effective_date').eq('currency_pair', 'USD/ETB').order('effective_date', { ascending: false }).limit(1),
        // GRN pending
        (supabase.from('grn' as any).select('id', { count: 'exact', head: true }) as any).eq('status', 'pending'),
        // Procurements for 6-month trend (paginated to avoid 1000-row limit)
        fetchAllProcurementsInRange(supabase, sixMonthsAgo, undefined, 'total_amount, created_at'),
      ] as const);

      const procurements = results[0];
      const expensesThisMonth = results[1];
      const expensesLastMonth = results[2] as any;
      const paymentRequests = results[3];
      const inventoryStock = results[4];
      const farms = results[5];
      const harvests = results[6];
      const users = results[7];
      const alerts = results[8];
      const qualityChecks = results[9] as any;
      const b2bThisMonth = results[10];
      const b2bLastMonth = results[11] as any;
      const exchangeRateResult = results[12] as any;
      const grnPending = results[13] as any;
      const procTrendData = results[14] as any[];

      // Procurement pipeline
      const procData = procurements.data || [];
      const procurementsByStatus: Record<string, number> = {};
      procData.forEach(p => {
        procurementsByStatus[p.status] = (procurementsByStatus[p.status] || 0) + 1;
      });
      const totalProcurementValue = procData.reduce((s, p) => s + (p.total_amount || 0), 0);
      const monthlyProcurementValue = procData
        .filter(p => p.created_at >= thisMonthStart)
        .reduce((s, p) => s + (p.total_amount || 0), 0);

      // 6-month procurement trend using shared UTC bucketing + paginated data
      const procTrendRows = Array.isArray(procTrendData) ? procTrendData : [];
      procTrendRows.forEach((p: any) => {
        assignToBucket(trendBuckets, p.created_at, p.total_amount || 0);
      });
      const procurementTrend: MonthlyTrendPoint[] = trendBuckets.map(b => ({ month: b.label, value: b.totalValue }));

      // Finance
      const totalExpensesThisMonth = (expensesThisMonth.data || []).reduce((s, e) => s + (e.total_amount || 0), 0);
      const totalExpensesLastMonth = (expensesLastMonth.data || []).reduce((s: number, e: any) => s + (e.total_amount || 0), 0);
      const prData = paymentRequests.data || [];
      const pendingPaymentRequests = prData.filter(pr => pr.status === 'pending_approval').length;
      const pendingPaymentAmount = prData.filter(pr => pr.status === 'pending_approval').reduce((s, pr) => s + (pr.total_amount || 0), 0);
      const approvedAwaitingPayment = prData.filter(pr => pr.status === 'approved').length;

      // B2B Sales
      const b2bSalesThisMonth = (b2bThisMonth.data || []).reduce((s, e: any) => s + (e.total_revenue || 0), 0);
      const b2bSalesLastMonth = (b2bLastMonth.data || []).reduce((s: number, e: any) => s + (e.total_revenue || 0), 0);

      // Inventory
      const stockData = inventoryStock.data || [];
      const totalProducts = stockData.length;
      const lowStockCount = stockData.filter(s => s.reorder_level > 0 && s.current_quantity > 0 && s.current_quantity <= s.reorder_level).length;
      const outOfStockCount = stockData.filter(s => s.current_quantity <= 0).length;

      // Farms & Harvests
      const totalFarms = farms.count || 0;
      const harvestData = harvests.data || [];
      const activeHarvests = harvestData.filter(h => ['planned', 'in_progress'].includes(h.status)).length;

      // Users & Alerts
      const totalUsers = users.count || 0;
      const alertData = alerts.data || [];
      const unreadAlerts = alertData.filter(a => !a.is_read).length;
      const criticalAlerts = alertData.filter(a => !a.is_read && !a.action_taken).slice(0, 5);

      // Quality
      const pendingQualityChecks = qualityChecks.data?.length || 0;

      // Exchange rate
      const exData = exchangeRateResult.data;
      const latestExchangeRate = exData && exData.length > 0
        ? { rate: exData[0].rate, currency_pair: exData[0].currency_pair, effective_date: exData[0].effective_date }
        : null;

      // GRN
      const pendingGRNCount = grnPending.count || 0;

      return {
        procurementsByStatus,
        totalProcurementValue,
        monthlyProcurementValue,
        recentProcurements: procData.slice(0, 8),
        procurementTrend,
        totalExpensesThisMonth,
        totalExpensesLastMonth,
        pendingPaymentRequests,
        pendingPaymentAmount,
        approvedAwaitingPayment,
        b2bSalesThisMonth,
        b2bSalesLastMonth,
        totalProducts,
        lowStockCount,
        outOfStockCount,
        totalStockValue: 0,
        totalFarms,
        activeHarvests,
        totalUsers,
        unreadAlerts,
        criticalAlerts,
        pendingQualityChecks,
        latestExchangeRate,
        pendingGRNCount,
      };
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
